/**
 * CancelFlowModal — Paket C (premium-checkout-redesign)
 *
 * Retention-Dialog vor der eigentlichen Kündigung (Design-Referenz:
 * subscription.html #cancel-ov). Zwei Alternativen:
 *  (a) auf einen kleineren Tarif wechseln — schliesst den Dialog und lässt
 *      den Aufrufer den Rechner (PlanChangeCalculator) auf diesen Plan setzen.
 *  (b) 12 Monate mit -30% bleiben — nur sichtbar, wenn das Backend für die
 *      12-Monats-Laufzeit tatsächlich einen Preis liefert (Ehrlichkeitsregel).
 * Danach der eigentliche Kündigungs-Text + Bestätigung, mit einem
 * "erledigt"-Zustand, der den Widerruf erwähnt.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { DemoGuardedButton } from '../../components/DemoGuardedButton'
import { activeBcp47 } from '../../lib/locale'
import { formatMoney } from '../premium/planMath'
import type { DurationMonths } from './subscriptionUtils'

function formatMonths(months: number): string {
  return months.toLocaleString(activeBcp47(), { maximumFractionDigits: 1, minimumFractionDigits: 1 })
}

type CancelFlowModalProps = {
  currentPlanName: string
  currentPlanId: string
  periodEndLabel: string
  currency: string
  creditAmount: number
  smallerPlanName: string | null
  smallerPlanId: string | null
  smallerPlanMonths: number | null
  twelveMonthMonthly: number | null
  freeMobile: number
  freeIot: number
  isMutating: boolean
  onSelectDowngrade: (planId: string) => void
  onCancelConfirmed: () => Promise<void>
  onClose: () => void
}

export function CancelFlowModal({
  currentPlanName,
  currentPlanId,
  periodEndLabel,
  currency,
  creditAmount,
  smallerPlanName,
  smallerPlanId,
  smallerPlanMonths,
  twelveMonthMonthly,
  freeMobile,
  freeIot,
  isMutating,
  onSelectDowngrade,
  onCancelConfirmed,
  onClose,
}: CancelFlowModalProps) {
  const { t } = useTranslation('subscription')
  const navigate = useNavigate()
  const [done, setDone] = useState(false)

  async function handleConfirmCancel() {
    await onCancelConfirmed()
    setDone(true)
  }

  function handleTwelveMonthOffer() {
    onClose()
    void navigate({
      to: '/checkout',
      search: { plan: currentPlanId, term: 12 as DurationMonths },
    })
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancel-flow-title"
      data-testid="cancel-flow-modal"
      className="subscription-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="subscription-modal">
        {!done ? (
          <>
            <h3 id="cancel-flow-title" style={{ margin: 0 }}>
              {t('cancelFlowModal.title')}
            </h3>
            <p className="muted" style={{ fontSize: 13, margin: '8px 0 16px' }}>
              {t('cancelFlowModal.intro')}
            </p>

            {smallerPlanId && smallerPlanName && smallerPlanMonths != null && (
              <button
                type="button"
                className="ret-opt"
                data-testid="retention-downgrade"
                onClick={() => onSelectDowngrade(smallerPlanId)}
              >
                <span className="ric" aria-hidden="true">↓</span>
                <span>
                  <b style={{ fontSize: 13, display: 'block' }}>
                    {t('cancelFlowModal.downgradeOption.title')}
                  </b>
                  <span className="muted" style={{ fontSize: 12 }}>
                    {t('cancelFlowModal.downgradeOption.subtitle', {
                      plan: smallerPlanName,
                      credit: formatMoney(creditAmount, currency),
                      months: formatMonths(smallerPlanMonths),
                    })}
                  </span>
                </span>
              </button>
            )}

            {twelveMonthMonthly != null && (
              <button
                type="button"
                className="ret-opt"
                data-testid="retention-twelve-month"
                onClick={handleTwelveMonthOffer}
              >
                <span className="ric" aria-hidden="true">★</span>
                <span>
                  <b style={{ fontSize: 13, display: 'block' }}>
                    {t('cancelFlowModal.twelveMonthOption.title')}
                  </b>
                  <span className="muted" style={{ fontSize: 12 }}>
                    {t('cancelFlowModal.twelveMonthOption.subtitle', {
                      price: formatMoney(twelveMonthMonthly, currency),
                    })}
                  </span>
                </span>
              </button>
            )}

            <div className="divider" />

            <p className="muted" style={{ fontSize: 12, margin: '0 0 12px' }}>
              {t('cancelFlowModal.finalText', {
                plan: currentPlanName,
                date: periodEndLabel,
                mobile: freeMobile,
                iot: freeIot,
              })}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-primary" onClick={onClose} data-testid="cancel-flow-keep">
                {t('cancelFlowModal.keepButton')}
              </button>
              <DemoGuardedButton
                className="btn btn-ghost"
                style={{ color: 'var(--danger)' }}
                onClick={() => { void handleConfirmCancel() }}
                disabled={isMutating}
                data-testid="cancel-flow-confirm"
              >
                {isMutating
                  ? t('cancelFlowModal.confirmButtonLoading')
                  : t('cancelFlowModal.confirmButton')}
              </DemoGuardedButton>
            </div>
          </>
        ) : (
          <>
            <div className="hint" style={{ marginBottom: 12 }} data-testid="cancel-flow-done">
              {t('cancelFlowModal.done', { date: periodEndLabel })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn" onClick={onClose} data-testid="cancel-flow-close">
                {t('common:actions.close')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
